import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {LightningBoltIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Dropdown from "components/Dropdown";
import {Trigger} from "types/Trigger";
import {useNavigate} from "react-router-dom";
import TriggerType from "types/TriggerType";
import DeleteTrigger from '../Delete';
import BooleanIndicator from "components/BooleanIndicator";
import {TimeConstraint} from "types/TriggerTimings";

// Map trigger types to their UI labels
const triggerTypeLabels: Record<TriggerType, string> = {
    [TriggerType.ACTIVATION]: 'Activation',
    [TriggerType.CONTENT]: 'Content',
    [TriggerType.TIME]: 'Time',
    [TriggerType.VISIT]: 'Visit',
    [TriggerType.UNKNOWN]: 'Unknown',
}

function triggerTimes(trigger: Trigger) {
    return Object
        .entries(trigger.timings)
        .filter(([key]) => key !== 'timezone') as Array<[string, TimeConstraint]>
}

// Check if a trigger has specific timings
function hasTimings(trigger: Trigger): boolean {
    return triggerTimes(trigger)
        .some(([,constraint]) => constraint.frequency !== 'always');
}

function TriggerList() {
    const [page, setPage] = useState<number>(1);
    const result = useQuery(['triggers.all', {page}], () => ngage.triggers.all(page), {keepPreviousData: true})

    const [deletingTrigger, setDeletingTrigger] = useState<Trigger | null>(null);

    const navigate = useNavigate()

    // Prefetch next and previous pages
    prefetchNearbyPages('triggers.all', page, ngage.triggers.all)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/triggers/new')}>New</Button>

    return (
        <Page
            title="Triggers"
            subTitle='Automated actions based on app user engagement'
            actions={actions}
            breadcrumbs={{'Engagement': '/', 'Triggers': '/triggers'}}
        >
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        {/*<TableHeader>Type</TableHeader>*/}
                        <TableHeader alignment={TableData.alignments.RIGHT}>Actions</TableHeader>
                        <TableHeader alignment={TableData.alignments.CENTER}>Time Restricted</TableHeader>
                        <TableHeader alignment={TableData.alignments.CENTER}>Enabled</TableHeader>
                        <TableHeader>Added</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>
                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={7}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <LightningBoltIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No triggers</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((trigger, index) => (
                        <TableRow key={trigger.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        <Dropdown position="left">
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/triggers/${trigger.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/triggers/${trigger.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingTrigger(trigger)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <LightningBoltIcon className="h-5 w-5 mr-2 text-gray-500" /> {trigger.name}
                                    </div>
                                </div>
                            </TableData>
                            {/*<TableData>*/}
                            {/*    {triggerTypeLabels[trigger.trigger_type]}*/}
                            {/*</TableData>*/}
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {trigger.action_count}
                            </TableData>
                            <TableData alignment={TableData.alignments.CENTER}>
                                <BooleanIndicator value={hasTimings(trigger)} />
                            </TableData>
                            <TableData alignment={TableData.alignments.CENTER}>
                                <BooleanIndicator value={trigger.is_enabled} />
                            </TableData>
                            <TableData>{formattedDateTime(trigger.created_at)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/triggers/${trigger.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/triggers/${trigger.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingTrigger(trigger)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteTrigger
                                trigger={deletingTrigger}
                                show={trigger === deletingTrigger}
                                onCancel={() => setDeletingTrigger(null)}
                                onDelete={() => navigate('/triggers')}
                                onDeleteFail={() => setDeletingTrigger(null)}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default TriggerList;